/* global vis */
import React, { Component } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { connect } from 'react-redux';
import CardPage from '../../../commonModules/layouts/CardPage';
import Currency from '../../../commonModules/Currency';
import { isEmpty } from 'lodash';

// Component wrapper to use hooks
const TransferGraphWrapper = (props) => {
    const { transferId } = useParams();
    
    // Note: Environment check is handled by the backend API
    // The backend will return 403 if in production environment
    
    return <TransferGraph transferId={transferId} {...props} />;
};

class TransferGraph extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            graphData: null,
            network: null,
            selectedTransfer: null
        };
        this.networkContainerRef = React.createRef();
    }

    componentDidMount() {
        // Load vis.js from CDN first
        this.loadVisJs();
        
        if (this.props.transferId) {
            this.loadGraphData(this.props.transferId);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // If graphData changed and vis.js is loaded, render the graph
        if (this.state.graphData && 
            this.state.graphData !== prevState.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
        
        // If vis.js just loaded and we have graphData, render it
        if (prevState.loading && !this.state.loading && 
            this.state.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
    }

    loadVisJs() {
        if (typeof window.vis !== 'undefined') {
            return; // Already loaded
        }

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vis-network/standalone/umd/vis-network.min.js';
        script.onload = () => {
            if (this.state.graphData) {
                this.renderGraph(this.state.graphData);
            }
        };
        document.head.appendChild(script);
    }

    async loadGraphData(transferId) {
        this.setState({ loading: true, error: null });

        try {
            const response = await fetch(`/api/transfers/graph/${transferId}`, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Read response once - check content type first
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            let data;
            if (isJson) {
                // Read the response body once
                const responseText = await response.text();
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON:', parseError, 'Response:', responseText);
                    throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
                }
            } else {
                // Non-JSON response
                const text = await response.text();
                throw new Error(`Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`);
            }

            // Now check if it's an error response
            if (!response.ok) {
                const errorMessage = data.error || `Erreur HTTP ${response.status}`;
                // Include nodeEnv in error message if provided by backend
                if (data.nodeEnv) {
                    throw new Error(`${errorMessage} (NODE_ENV: ${data.nodeEnv})`);
                }
                throw new Error(errorMessage);
            }

            console.log('Graph data received:', data);
            
            // Validate response data
            if (!data) {
                throw new Error('Aucune donnée reçue du serveur');
            }
            
            if (!data.nodes || !Array.isArray(data.nodes)) {
                console.error('Invalid graph data structure:', data);
                throw new Error(`Format de données invalide: nodes manquant ou invalide. Reçu: ${JSON.stringify(data)}`);
            }

            this.setState({ graphData: data, loading: false });
            
            // Render graph if vis.js is loaded
            if (typeof window.vis !== 'undefined') {
                this.renderGraph(data);
            } else {
                // Wait a bit for vis.js to load
                const checkVisLoaded = setInterval(() => {
                    if (typeof window.vis !== 'undefined') {
                        clearInterval(checkVisLoaded);
                        this.renderGraph(data);
                    }
                }, 100);
                
                // Timeout after 5 seconds
                setTimeout(() => {
                    clearInterval(checkVisLoaded);
                    if (typeof window.vis === 'undefined') {
                        this.setState({ 
                            error: 'Erreur: vis.js n\'a pas pu être chargé',
                            loading: false 
                        });
                    }
                }, 5000);
            }
        } catch (error) {
            console.error('Error loading graph data:', error);
            this.setState({ 
                error: error.message || 'Erreur lors du chargement des données du graphique',
                loading: false 
            });
        }
    }

    renderGraph(data) {
        if (!this.networkContainerRef.current) {
            return;
        }

        // Validate data structure
        if (!data || typeof data !== 'object') {
            console.error('Invalid graph data:', data);
            this.setState({ 
                error: 'Données du graphique invalides',
                loading: false 
            });
            return;
        }

        const graphNodes = data.nodes || [];
        const graphEdges = data.edges || [];

        // Validate that we have nodes
        if (!Array.isArray(graphNodes) || graphNodes.length === 0) {
            console.error('No nodes found in graph data:', data);
            this.setState({ 
                error: 'Aucun transfer trouvé pour ce graphique',
                loading: false 
            });
            return;
        }

        // Check if vis.js is loaded
        if (typeof window.vis === 'undefined') {
            console.error('vis.js is not loaded');
            return;
        }

        // Log nodes for debugging
        console.log('Total nodes in graph:', graphNodes.length);
        console.log('Total edges in graph:', graphEdges.length);
        console.log('Node IDs:', graphNodes.map(n => n.transfer_id.substring(0, 8) + '...'));
        
        // Store node data mapping for later access
        const nodeDataMap = {};
        graphNodes.forEach(node => {
            nodeDataMap[node.transfer_id] = node;
        });

        // Create nodes for vis.js
        const nodes = new window.vis.DataSet(
            graphNodes.map(node => ({
                id: node.transfer_id,
                label: `${node.shop_from_name}\n→ ${node.shop_to_name}\n${node.date ? new Date(node.date).toLocaleDateString() : ''}\n${this.formatCurrencyString(node.total_ttc || 0)}`,
                title: `Transfer: ${node.transfer_id}\nType: ${node.nature}\nDate: ${node.date}\nMontant TTC: ${this.formatCurrencyString(node.total_ttc || 0)}`,
                color: this.getNodeColor(node.nature),
                shape: 'box',
                font: {
                    size: 13,
                    color: 'white',
                    bold: true
                },
                borderWidth: 2,
                shadow: {
                    enabled: true
                }
            }))
        );

        // Create edges for vis.js
        const edges = new window.vis.DataSet(
            graphEdges.map(edge => ({
                from: edge.from,
                to: edge.to,
                arrows: 'to',
                color: { color: '#2196F3' },
                width: 2,
                smooth: {
                    type: 'curvedCW',
                    roundness: 0.2
                }
            }))
        );

        // Network options
        const options = {
            layout: {
                hierarchical: {
                    enabled: true,
                    direction: 'LR', // Left to Right
                    sortMethod: 'directed',
                    levelSeparation: 500,
                    nodeSpacing: 250,
                    treeSpacing: 300,
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true
                }
            },
            physics: {
                enabled: false
            },
            edges: {
                font: {
                    size: 12,
                    align: 'middle'
                },
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 1.2
                    }
                }
            },
            nodes: {
                widthConstraint: {
                    minimum: 200,
                    maximum: 300
                },
                margin: 15
            },
            interaction: {
                hover: true,
                tooltipDelay: 100,
                dragNodes: true,
                dragView: true,
                zoomView: true
            }
        };

        // Create network
        const container = this.networkContainerRef.current;
        const networkData = { nodes, edges };
        const network = new window.vis.Network(container, networkData, options);

        // Handle node clicks
        network.on('click', (params) => {
            if (params.nodes.length > 0) {
                const nodeId = params.nodes[0];
                const node = nodeDataMap[nodeId];
                if (node) {
                    this.setState({ selectedTransfer: node });
                }
            } else {
                this.setState({ selectedTransfer: null });
            }
        });

        // Center view after rendering
        network.once('afterDrawing', () => {
            network.fit({
                animation: {
                    duration: 500,
                    easingFunction: 'easeInOutQuad'
                }
            });
        });

        this.setState({ network });
    }

    formatCurrencyString(value) {
        const currency = this.props.currency || 'Euro';
        const numValue = parseFloat(value) || 0;
        
        let formattedValue;
        let symbol;
        
        if (currency === 'Euro') {
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = '€';
        } else {
            formattedValue = Math.round(numValue).toLocaleString('fr-FR');
            symbol = 'FCFA';
        }
        
        return `${formattedValue} ${symbol}`;
    }

    getNodeColor(nature) {
        // Color code by transfer type
        const colors = {
            'reapprovisionnement': '#4CAF50',
            'reservation': '#2196F3',
            'installation': '#FF9800',
            'vente': '#E91E63',
            'payg': '#9C27B0',
            'achat': '#00BCD4',
            'commercial': '#795548',
            'reapprovisionnement_ext': '#607D8B'
        };
        return colors[nature] || '#757575';
    }

    changeLayout(direction) {
        if (this.state.network) {
            const layoutOptions = {
                layout: {
                    hierarchical: {
                        enabled: true,
                        direction: direction,
                        sortMethod: 'directed',
                        levelSeparation: direction === 'UD' || direction === 'DU' ? 300 : 500,
                        nodeSpacing: direction === 'UD' || direction === 'DU' ? 200 : 250,
                        treeSpacing: 300,
                        blockShifting: true,
                        edgeMinimization: true,
                        parentCentralization: true
                    }
                }
            };
            
            this.state.network.setOptions(layoutOptions);
            
            setTimeout(() => {
                this.state.network.fit({
                    animation: {
                        duration: 500,
                        easingFunction: 'easeInOutQuad'
                    }
                });
            }, 100);
        }
    }

    render() {
        const { loading, error, graphData, selectedTransfer } = this.state;

        return (
            <CardPage>
                <Box p={2}>
                    <Typography variant="h4" gutterBottom>
                        Graphique du Transfer avec Parents
                    </Typography>

                    {this.props.transferId && (
                        <Typography variant="body1" color="textSecondary" gutterBottom>
                            Transfer ID: {this.props.transferId}
                        </Typography>
                    )}

                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                            <Typography variant="body1" style={{ marginLeft: 16 }}>
                                Chargement du graphique...
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" style={{ marginTop: 16 }}>
                            {error}
                        </Alert>
                    )}

                    {graphData && !loading && (
                        <>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    {graphData.nodes ? graphData.nodes.length : 0} transfer(s) dans le graphique
                                    {graphData.edges && `, ${graphData.edges.length} relation(s)`}
                                </Typography>
                                {graphData.nodes && graphData.nodes.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        IDs: {graphData.nodes.slice(0, 3).map(n => n.transfer_id.substring(0, 8)).join(', ')}
                                        {graphData.nodes.length > 3 && '...'}
                                    </Typography>
                                )}
                            </Box>

                            <Box mb={2}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('LR')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (G → D)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('UD')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Verticale (H → B)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('RL')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (D → G)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => {
                                        if (this.state.network) {
                                            this.state.network.fit();
                                        }
                                    }}
                                >
                                    Recentrer
                                </Button>
                            </Box>

                            <Paper style={{ padding: 16, marginBottom: 16 }}>
                                <div 
                                    ref={this.networkContainerRef}
                                    style={{ 
                                        width: '100%', 
                                        height: '600px', 
                                        border: '1px solid #ddd',
                                        borderRadius: 4
                                    }}
                                />
                            </Paper>

                            {selectedTransfer && (
                                <Paper style={{ padding: 16, marginTop: 16 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Détails du Transfer
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>ID:</strong> {selectedTransfer.transfer_id}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Type:</strong> {selectedTransfer.nature}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Date:</strong> {selectedTransfer.date ? new Date(selectedTransfer.date).toLocaleDateString() : 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>De:</strong> {selectedTransfer.shop_from_name} ({selectedTransfer.shop_from})
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Vers:</strong> {selectedTransfer.shop_to_name} ({selectedTransfer.shop_to})
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Total HT:</strong> <Currency value={selectedTransfer.total_ht || 0} />
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Total TTC:</strong> <Currency value={selectedTransfer.total_ttc || 0} />
                                    </Typography>
                                    {selectedTransfer.transport > 0 && (
                                        <Typography variant="body2">
                                            <strong>Transport:</strong> <Currency value={selectedTransfer.transport} />
                                        </Typography>
                                    )}
                                    {selectedTransfer.taxes > 0 && (
                                        <Typography variant="body2">
                                            <strong>Taxes:</strong> <Currency value={selectedTransfer.taxes} />
                                        </Typography>
                                    )}
                                    <Typography variant="body2">
                                        <strong>Nombre de produits:</strong> {selectedTransfer.product_count || 0}
                                    </Typography>
                                    {selectedTransfer.is_root && (
                                        <Typography variant="body2" color="primary">
                                            <strong>✓ Transfer racine (pas de parents)</strong>
                                        </Typography>
                                    )}
                                </Paper>
                            )}
                        </>
                    )}

                    {!this.props.transferId && !loading && (
                        <Alert severity="info">
                            Aucun ID de transfer fourni. Veuillez accéder à cette page via une URL avec un ID de transfer.
                        </Alert>
                    )}
                </Box>
            </CardPage>
        );
    }
}

// Map Redux state to props
function mapStateToProps(state) {
    return {
        currency: state.currency?.currency || 'Euro'
    };
}

// Connect TransferGraph to Redux for currency
const ConnectedTransferGraph = connect(mapStateToProps)(TransferGraph);

// Update wrapper to use connected component
const TransferGraphWrapperConnected = (props) => {
    const { transferId } = useParams();
    
    // Note: Environment check is handled by the backend API
    // The backend will return 403 if in production environment
    
    return <ConnectedTransferGraph transferId={transferId} {...props} />;
};

export default TransferGraphWrapperConnected;
